<template>
  <div class="standard-peaks">
    <div v-if="peaks && peaks.length > 0" class="standard-peaks__wrapper-table">
      <table class="table-peaks">
        <thead class="table-peaks__thead">
          <tr>
            <th align="left">#</th>
            <th align="left" style="white-space: nowrap">
              Time <span class="table-peaks__unit">(min)</span>
            </th>
            <th align="right" style="white-space: nowrap; text-align: right">
              Peak area <span class="table-peaks__unit">(mAU * min)</span>
            </th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody class="table-peaks__tbody">
          <template v-for="(i, index) in sortedPeaks">
            <tr
              v-if="!i.is_integrated"
              :key="`${index}-main`"
              :class="{
                'tr--selected': i.id === selectedPeakID,
                'tr--highlighted': i.id === highlightedPeakId,
              }"
              @mouseenter="onMouseEnter(i.id)"
              @mouseleave="onMouseLeave(i.id)"
            >
              <td>
                <span class="font-monospace font-40">{{ index + 1 }}</span>
              </td>
              <td class="font-monospace">
                {{ i.apex || '—' }}
              </td>
              <td colspan="3" class="integration-error">
                <span class="integration-error__text">{{
                  i.integration_failure_reason || 'Integration failed'
                }}</span>
              </td>
            </tr>
            <!-- Integrated peak row -->
            <tr
              v-else
              :key="`${index}-main`"
              :class="{
                'tr--selected': i.id === selectedPeakID,
                'tr--highlighted': i.id === highlightedPeakId,
              }"
              @mouseenter="onMouseEnter(i.id)"
              @mouseleave="onMouseLeave(i.id)"
            >
              <td>
                <span class="font-monospace" :class="{ 'font-40': i.id !== selectedPeakID }">
                  {{ index + 1 }}
                </span>
              </td>
              <td class="font-monospace">
                {{ i.apex || '—' }}
              </td>
              <td align="right" class="font-monospace font-bold text-info">
                {{ i.area || '—' }}
              </td>
              <td style="padding-left: 64px">
                <div v-if="i.id === selectedPeakID">
                  <i class="material-icons material-icon--16">check</i>
                  Selected
                </div>
              </td>
              <td style="padding-left: 0">
                <Btn
                  v-if="peakIdToShowFullInformation === i.id"
                  type="transparent"
                  padding="xs"
                  class="table-peaks__btn-details"
                  @click="hideFullPeakInformation"
                >
                  Hide details
                </Btn>
                <Btn
                  v-else
                  type="transparent"
                  padding="xs"
                  class="table-peaks__btn-details"
                  @click="showShowFullPeakInformation(i.id)"
                >
                  Show more
                </Btn>
              </td>
            </tr>
            <template v-if="peakIdToShowFullInformation === i.id && i.is_integrated">
              <PeakDetails v-if="i" :key="`${index}-details`" :peak="i" :colspan="5" />
              <!-- Stub for bg styling-->
              <tr :key="`${index}-stub`"></tr>
            </template>
          </template>
        </tbody>
      </table>
    </div>
    <NothingThereComponent v-else style="margin-top: 64px"> No peaks </NothingThereComponent>
  </div>
</template>

<script>
  import _ from 'lodash';
  import NothingThereComponent from 'components/element/NothingThereComponent.vue';
  import Btn from '@/uikitBase/btns/Btn.vue';
  import PeakDetails from '@/components/blocks/charts/chromatogramNew/private/PeakDetails.vue';
  import { apiCalibrations } from '@/api/graphql/cloud/calibrations';

  export default {
    name: 'StandardPeaksComponent',

    components: {
      PeakDetails,
      NothingThereComponent,
      Btn,
    },

    props: {
      ppeaks: Array,
      measurementSocket: Object,
      injectionSocket: Object,
      highlightedPeakId: Number,
    },

    data() {
      return {
        peaks: this.modifyPeaks(this.ppeaks),
        peakIdToShowFullInformation: null,
        selectedPeakID: null,
        selectedPeakSubscription: null,
        selectedPeakSubscriptionKey: null,
      };
    },

    computed: {
      sortedPeaks() {
        if (!this.peaks) return [];
        return [...this.peaks].sort((a, b) => a.apex - b.apex);
      },
    },

    watch: {
      ppeaks() {
        this.setPeaks(this.ppeaks);
        this.refreshSelectedPeakSubscription();
      },
      selectedPeakID: {
        async handler(newValue, oldValue) {
          await this.$nextTick();
          oldValue != null && this.emitSelectionEvent(oldValue, false);
          newValue != null && this.emitSelectionEvent(newValue, true);
        },
        immediate: true,
      },
    },

    created() {
      this.refreshSelectedPeakSubscription();
    },

    beforeDestroy() {
      this.unsubscribeSelectedPeak();
    },

    methods: {
      emitSelectionEvent(peakId, isSelected) {
        this.$emit('selection', { peakId, isSelected });
      },

      getPeakIdsForSubscription() {
        if (!Array.isArray(this.ppeaks) || this.ppeaks.length === 0) {
          return [];
        }

        const ids = this.ppeaks
          .map((p) => p && p.id)
          .filter((id) => typeof id === 'number' && Number.isFinite(id));

        return [...new Set(ids)].sort((a, b) => a - b);
      },

      async unsubscribeSelectedPeak() {
        if (!this.selectedPeakSubscription) return;
        try {
          await this.selectedPeakSubscription.unsubscribe();
        } catch (e) {
          // ignore transient unsubscribe errors
        } finally {
          this.selectedPeakSubscription = null;
        }
      },

      async refreshSelectedPeakSubscription() {
        const peakIds = this.getPeakIdsForSubscription();
        const key = peakIds.join(',');

        if (!key) {
          this.selectedPeakSubscriptionKey = null;
          await this.unsubscribeSelectedPeak();
          this.selectedPeakID = null;
          return;
        }

        if (this.selectedPeakSubscriptionKey === key) {
          return;
        }

        this.selectedPeakSubscriptionKey = key;
        await this.unsubscribeSelectedPeak();

        this.selectedPeakSubscription = apiCalibrations.subscribeForSelectedPeakByPeakIds(
          peakIds,
          (selectedPeakId) => {
            this.selectedPeakID = selectedPeakId;
          },
        );
      },

      modifyPeaks(peaks) {
        if (peaks == null) return null;
        // eslint-disable-next-line no-param-reassign
        peaks = _.cloneDeep(peaks);
        for (let i = 0; i < peaks.length; i++) {
          // eslint-disable-next-line no-param-reassign
          peaks[i].apex = peaks[i].apex != null ? peaks[i].apex.toFixed(3) : null;
          // eslint-disable-next-line no-param-reassign
          peaks[i].area = peaks[i].area != null ? peaks[i].area.toFixed(3) : null;
        }
        return peaks;
      },

      onMouseEnter(peakId) {
        this.emitSelectionEvent(peakId, true);
      },
      onMouseLeave(peakId) {
        if (this.selectedPeakID !== peakId) {
          this.emitSelectionEvent(peakId, false);
        }
      },

      setPeaks(peaks) {
        this.peaks = this.modifyPeaks(peaks);
      },

      showShowFullPeakInformation(peakId) {
        this.peakIdToShowFullInformation = peakId;
      },
      hideFullPeakInformation() {
        this.peakIdToShowFullInformation = null;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .standard-peaks {
    &__wrapper-table {
      overflow-x: auto;
    }

    &__label-integration-type {
      font-weight: $weight-bold;
    }
  }

  .table-peaks {
    width: 100%;
    height: 1px;
  }

  .table-peaks button {
    display: block;
    visibility: hidden;
    min-width: 16px;
    padding: 4px 8px;
    margin: 0;
    background: transparent;
    color: $color-text-primary;
  }

  .table-peaks button:hover {
    background: #00000022;
  }

  .table-peaks button:active {
    background: #00000044;
  }

  .table-peaks input {
    border: 0 solid transparent;
    border-bottom-width: 2px;
    background: transparent;
    border-radius: 3px;
    padding: 6px 6px 0 6px;
    font-size: 13px;
  }

  .table-peaks input:focus {
    border-width: 0 0 2px 0;
    border-color: $color-bg-primary;
    outline: none !important;
  }

  .table-peaks__thead th {
    font-size: 13px;
    font-weight: bold;
    color: #00000066;
    padding: 2px 8px 6px 8px;
  }

  .table-peaks__thead th:first-child {
    padding-left: 32px;
  }

  .table-peaks__thead th:last-child {
    padding-right: 32px;
  }

  .table-peaks__unit {
    font-size: 12px;
    font-weight: normal;
    opacity: 0.7;
    margin-left: 4px;
  }

  .table-peaks__tbody td {
    font-size: 13px;
    white-space: nowrap;
    padding: 4px 8px 3px 8px;
    width: 1px;
  }

  .table-peaks__tbody td:first-child {
    padding-left: 32px;
  }

  .table-peaks__tbody td:last-child {
    padding-right: 32px;
  }

  .table-peaks__tbody tr:hover button {
    visibility: visible;
  }

  .table-peaks__tbody tr:nth-child(2n-1) {
    background-color: #00000008;
  }

  .table-peaks__tbody tr:hover,
  .table-peaks__tbody tr.tr--highlighted {
    background-color: #00000011;
  }

  .table-peaks__tbody input {
    padding-bottom: 4px;
    height: 24px;
  }

  .table-peaks__tbody tr input::placeholder {
    color: #00000000;
  }

  .table-peaks__tbody tr:hover input::placeholder {
    color: #00000066;
  }

  .tr--selected {
    background: #2a2398 !important;
    color: #ffffff !important;
  }

  .tr--selected td {
    background: #2a2398 !important;
    color: #ffffff !important;
  }

  .tr--selected button {
    color: #ffffff !important;
  }

  .integration-error {
    color: $color-text-danger;
    font-style: italic;
    text-align: left;
    padding-left: 12px;

    &__text {
      font-size: $size-xs;
    }
  }
</style>
